import React from "react";
import { RouteComponentProps } from "react-router";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Row, Col, Button, Modal, message, Spin, Table } from "antd";
import Card from "../components/Card";
import { NavLink } from "react-router-dom";
import { request } from "../helpers/utilities";
import { AxiosResponse } from "axios";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import * as Sentry from "@sentry/browser";
import { Product } from "../helpers/shared-interfaces";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  name: string;
  isLoading: boolean;
  id: string | number;
  showDialog: boolean;
  numberOfProducts: number;
  products: Product[];
}

interface RouteParams {
  id: string;
}

export const columns = [
  {
    title: "Products",
    dataIndex: "name",
    key: "Product",
    render(text: string, record: Product): JSX.Element {
      return (
        <NavLink to={`/products/${record.id}`} data-cy="test-user-details-link">
          {text}
        </NavLink>
      );
    },
  },
];

export function injectKeyProp(data: Product[]): Product[] {
  return data.map(
    (product: Product): Product => ({ ...product, key: product.id }),
  );
}

export const DELETE_PRODUCT_CATEGORY = `
  mutation DeleteProductCategory($id:ID!) {
    deleteProductCategory(id: $id ){
      result
      errors {
        property
        description
      }
    }
  }
`;

export const GET_PRODUCT_CATEGORY = `
  query GetProductCategory($id:ID!) {
    productCategory(id: $id ){
      id
      name
      products {
        id
        name
      }
    }
  }
`;

function ModalWrapper(
  showDialog: boolean,
  deleteProductCategory: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteProductCategory}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this product category?</div>
    </Modal>
  );
}

export default class ProductCategory extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      name: "",
      isLoading: true,
      id: "",
      showDialog: false,
      numberOfProducts: 0,
      products: []
    };
    this.deleteProductCategory = this.deleteProductCategory.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
    this.getProductCategoryData = this.getProductCategoryData.bind(this);
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  redirectToAllProductCategoriesPage = (): void => {
    this.props.history.push("/product-categories");
  };

  getProductCategoryData(): void {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_PRODUCT_CATEGORY,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            this.setState({
              name: data.data.productCategory?.name,
              numberOfProducts: data.data.productCategory?.products.length,
              products: data.data.productCategory.products,
            });
          } else {
            message.error("Failed to Get product category data");
          }
        });
      },
    );
  }

  handleInputChange(
    e: React.ChangeEvent<{
      value: string;
    }>,
  ): void {
    this.setState({ name: e.target.value });
  }

  async deleteProductCategory(): Promise<void> {
    interface Response {
      data: {
        deleteProductCategory: {
          result: boolean;
        };
      };
    }
    const { id } = this.state;

    this.setState({ isLoading: true });

    try {
      const { data }: AxiosResponse<Response> = await request({
        query: DELETE_PRODUCT_CATEGORY,
        variables: { id },
      });

      this.setState({ isLoading: false, showDialog: false });

      if (data.data.deleteProductCategory.result) {
        message.success("Product Category deleted successfuly");
        this.redirectToAllProductCategoriesPage();
      } else {
        message.error("Failed to delete product category");
      }
    } catch (error) {
      Sentry.captureException(error);
      message.error(`Request Failed. - ${generateDisplayErrorMessage(error)}`);
    }
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getProductCategoryData();
    });
  }

  render(): JSX.Element | null {
    const { isLoading, name, id, showDialog, numberOfProducts, products } = this.state;
    return (
      <Spin spinning={isLoading}>
        <Card>
          <div>
            {ModalWrapper(
              showDialog,
              this.deleteProductCategory,
              this.toggleDeleteConfirmationModal,
            )}
            <Row
              style={{
                marginBottom: "20px",
              }}
            >
              <Col
                style={{
                  textAlign: "left",
                }}
                span={12}
              >
                <div>Product Category Name</div>
                <div>
                  <h1
                    style={{
                      fontWeight: "bold",
                      fontSize: "25px",
                      marginBottom: "0px",
                    }}
                    data-cy="test-product-category-name"
                  >
                    {name?.toUpperCase()}
                  </h1>
                </div>
              </Col>

              <Col
                style={{
                  textAlign: "right",
                }}
                span={12}
              >
                <EntitledSection
                  entitlement={entitlements.ProductCategories.edit}
                >
                  <NavLink to={`/product-categories/${id}/edit?name=${name}`}>
                    <Button
                      icon={<EditOutlined />}
                      size="large"
                      style={{
                        backgroundColor: "  rgb(29, 55, 100)",
                        color: "white",
                        marginRight: "10px",
                      }}
                      data-cy="test-edit-product-category-button"
                    >
                      Edit
                    </Button>
                  </NavLink>
                </EntitledSection>
                {numberOfProducts < 1 && (
                  <EntitledSection
                    entitlement={entitlements.ProductCategories.delete}
                  >
                    <Button
                      icon={<DeleteOutlined />}
                      size="large"
                      onClick={this.toggleDeleteConfirmationModal}
                      data-cy="test-delete-product-category-button"
                    >
                      Delete
                    </Button>
                  </EntitledSection>
                )}
              </Col>
            </Row>
            <Table
              bordered={true}
              columns={columns}
              dataSource={injectKeyProp(products)}
            />
          </div>
        </Card>
      </Spin>
    );
  }
}
